import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppScopeStore } from '@frontend/scope';
import { usePreference } from '@frontend/user-preferences';
import {
  createDefaultRecentCallsFilters,
  normalizeOfficePhoneNumbers,
} from '../components/recent-calls/recent-calls-filters';
import { CallRecordsFiltersType } from './use-phone-config-state-store';

export const useRecentCallsFilters = () => {
  const { selectedOrgId } = useAppScopeStore();
  const [storedFiltersMap, setStoredFiltersMap] = usePreference('RecentCallsFilters', { omitDefaultValue: true });
  const hasMigratedStoredFiltersRef = useRef(false);

  const storedFilters = storedFiltersMap?.[selectedOrgId];

  const filters = useMemo(() => createDefaultRecentCallsFilters(storedFilters ?? {}), [storedFilters]);

  /**
   * This effect is here to fix potential issues with the stored filters in user preferences.
   * Specifically, it normalizes the `officePhoneNumbers` field to ensure that it is always an array of strings.
   * If the stored filters have not been migrated yet and contain an `officePhoneNumbers` field, this effect will normalize it and update the stored filters accordingly.
   *
   * We might be able to remove this in the future if we don't see this sentry alert any longer
   * https://weave-u5.sentry.io/issues/7642860083/events/e62c2e2a44014c7cab65db910edd5d11/
   */
  useEffect(() => {
    if (hasMigratedStoredFiltersRef.current || storedFilters === undefined) {
      return;
    }

    hasMigratedStoredFiltersRef.current = true;

    if (storedFilters.officePhoneNumbers === undefined) {
      return;
    }

    const normalizedOfficePhoneNumbers = normalizeOfficePhoneNumbers(storedFilters.officePhoneNumbers);
    const isUnchanged =
      storedFilters.officePhoneNumbers.length === normalizedOfficePhoneNumbers.length &&
      storedFilters.officePhoneNumbers.every(
        (phoneNumber, index) => phoneNumber === normalizedOfficePhoneNumbers[index]
      );

    if (isUnchanged) {
      return;
    }

    setStoredFiltersMap({
      ...storedFiltersMap,
      [selectedOrgId]: { ...storedFilters, officePhoneNumbers: normalizedOfficePhoneNumbers },
    });
  }, [selectedOrgId, setStoredFiltersMap, storedFilters, storedFiltersMap]);

  const setFilters = useCallback(
    (nextFilters: CallRecordsFiltersType) => {
      setStoredFiltersMap({ ...storedFiltersMap, [selectedOrgId]: createDefaultRecentCallsFilters(nextFilters) });
    },
    [selectedOrgId, setStoredFiltersMap, storedFiltersMap]
  );

  return {
    filters,
    setFilters,
  };
};
